import { useState, useCallback } from 'react';
import {
  ArrowPathIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import { copyTextToClipboard, sanitizeDownloadFilename, triggerBlobDownload } from './aidaClipboard';

type BotMessageActionsProps = {
  markdown: string;
  canRetry?: boolean;
  disabled?: boolean;
  onRetry?: () => void;
  trackingGroup?: string;
  onTrack?: (action: 'copy' | 'retry' | 'export_md') => void;
};

export default function BotMessageActions({
  markdown,
  canRetry = false,
  disabled = false,
  onRetry,
  onTrack,
}: BotMessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [exported, setExported] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!markdown.trim() || disabled) return;
    onTrack?.('copy');
    const ok = await copyTextToClipboard(markdown);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  }, [markdown, disabled, onTrack]);

  const handleExportMd = useCallback(() => {
    if (!markdown.trim() || disabled) return;
    onTrack?.('export_md');
    const filename = `${sanitizeDownloadFilename('aida-response')}.md`;
    triggerBlobDownload(markdown, filename, 'text/markdown;charset=utf-8');
    setExported(true);
    window.setTimeout(() => setExported(false), 2000);
  }, [markdown, disabled, onTrack]);

  const handleRetry = useCallback(() => {
    if (!canRetry || disabled || !onRetry) return;
    onTrack?.('retry');
    onRetry();
  }, [canRetry, disabled, onRetry, onTrack]);

  if (!markdown.trim() && !canRetry) return null;

  return (
    <div className="aida-msg__actions" role="toolbar" aria-label="Message actions">
      <button
        type="button"
        className="aida-msg__action-btn"
        onClick={handleCopy}
        disabled={disabled || !markdown.trim()}
        title={copied ? 'Copied' : 'Copy response'}
        aria-label={copied ? 'Copied' : 'Copy response'}
        data-demo-tour="aida-copy-response"
      >
        {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      </button>

      {canRetry && (
        <button
          type="button"
          className="aida-msg__action-btn"
          onClick={handleRetry}
          disabled={disabled}
          title="Retry response"
          aria-label="Retry response"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      )}

      <button
        type="button"
        className="aida-msg__action-btn"
        onClick={handleExportMd}
        disabled={disabled || !markdown.trim()}
        title={exported ? 'Exported' : 'Export as Markdown'}
        aria-label={exported ? 'Exported' : 'Export as Markdown'}
      >
        {exported ? <CheckIcon className="h-4 w-4" /> : <DocumentArrowDownIcon className="h-4 w-4" />}
      </button>
    </div>
  );
}
